import { Link } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { PageHeader } from '../../components/primitives.jsx';
import {
  StaggerReveal,
  IconDashboard,
  IconBriefcase,
  IconRoadmap,
  IconHome,
  IconGate,
  IconLineage,
} from '../../components/index.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { personaKeyFromRoles, primaryRoleLabel } from '../../lib/personaKey.js';
import './errors.redesign.css';

// Candidate recovery destinations. Guide-10 external UAT FINDING-015: the
// rendered list is permission-filtered per actor via rbac.ROUTE_PERMISSIONS —
// a denial page must never point a denied user at further denials.
const QUICK_LINK_CANDIDATES = [
  {
    to: '/executive',
    permissionKey: '/executive',
    label: 'Executive Command Center',
    desc: 'Mission pulse, predictive risk, and portfolio health.',
    Icon: IconDashboard,
  },
  {
    to: '/projects',
    permissionKey: '/projects',
    label: 'Projects',
    desc: 'Delivery health, gate readiness, and work items.',
    Icon: IconBriefcase,
  },
  {
    to: '/roadmap',
    permissionKey: '/roadmap',
    label: 'Strategic Roadmap',
    desc: 'Sequenced initiatives and milestone alignment.',
    Icon: IconRoadmap,
  },
  {
    to: '/admin/ingestion',
    permissionKey: '/admin/ingestion',
    label: 'Ingestion Center',
    desc: 'Stage, validate, and commit governed data.',
    Icon: IconGate,
  },
  {
    to: '/admin/lineage',
    permissionKey: '/admin/lineage',
    label: 'Lineage',
    desc: 'Trace every figure back to its source systems.',
    Icon: IconLineage,
  },
  {
    to: '/home',
    permissionKey: '/home',
    label: 'Home',
    desc: 'Your persona landing and recent activity.',
    Icon: IconHome,
  },
];

function reachableQuickLinks(roles) {
  return QUICK_LINK_CANDIDATES.filter((link) =>
    rbac.hasAnyRole(roles || [], rbac.ROUTE_PERMISSIONS[link.permissionKey] || []),
  ).slice(0, 4);
}

function initialsFor(name) {
  if (!name) return '??';
  return name.slice(0, 2).toUpperCase();
}

export function AccessDeniedPage() {
  const { user, roles } = useCurrentUser();
  const personaKey = Array.isArray(roles) && roles.length ? personaKeyFromRoles(roles) : null;
  const personaDef = personaKey ? rbac.PERSONA_DISPLAY[personaKey] : null;
  // Name the account's ACTUAL role (DEF-002) — the persona bucket is only a
  // fallback. A stale private mapper here once mislabeled Program Managers as
  // Pending Access; the shared personaKey helpers are the single source now.
  const roleLabel = primaryRoleLabel(roles) || personaDef?.label || null;
  const displayName = user?.displayName || null;

  return (
    <>
      <PageHeader
        eyebrow="403"
        title="Access denied"
        subtitle="Your current persona doesn't include permission for this view."
      />

      <StaggerReveal className="glass-panel dark error-hero" data-testid="error-hero-403">
        <div className="error-hero__lede">
          <img
            className="error-hero__seal"
            src="/brand/dha-seal.svg"
            alt="Defense Health Agency seal"
            data-testid="error-dha-seal"
          />
          <div className="error-hero__heading">
            <span className="error-hero__eyebrow">Error 403</span>
            <h2 className="error-hero__title">You don&rsquo;t have access to this surface</h2>
            <p className="error-hero__subtitle">
              This view requires permissions your current persona doesn&rsquo;t hold. If you believe
              you should have access, submit an eSAAR request and a steward will review it.
            </p>
          </div>
        </div>

        <div className="banner red" role="status" data-testid="error-banner">
          <span className="banner__icon" aria-hidden>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="4" y="10" width="16" height="10" rx="2" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
          </span>
          <span className="banner__body">
            <span className="banner__title">Permission required</span>
            <span className="banner__detail">
              This surface is locked for your current role. Request access below or return to a
              surface you can reach.
            </span>
          </span>
        </div>

        {(displayName || roleLabel) && (
          <div className="error-persona" data-testid="error-persona">
            <span className="error-persona__avatar" aria-hidden>
              {initialsFor(displayName || roleLabel)}
            </span>
            <span className="error-persona__meta">
              <span className="error-persona__label">Signed in as</span>
              <span className="error-persona__value" data-testid="error-persona-name">
                {displayName || 'PRIME user'}
              </span>
              {roleLabel ? (
                <span className="error-persona__role" data-testid="error-persona-role">
                  {roleLabel}
                </span>
              ) : null}
            </span>
          </div>
        )}

        <div className="error-hero__actions">
          <Link className="btn primary" to="/access/request">
            Request access
          </Link>
          <Link className="btn ghost" to="/home">
            Go home
          </Link>
        </div>
      </StaggerReveal>

      <section className="error-quicklinks" aria-label="Surfaces you can reach">
        <p className="error-quicklinks__title">Surfaces you can reach</p>
        <StaggerReveal className="error-quicklink-grid">
          {reachableQuickLinks(roles).map(({ to, label, desc, Icon }) => (
            <Link
              key={to}
              to={to}
              className="card interactive error-quicklink"
              data-testid={`error-quicklink-${to.replace(/\//g, '') || 'home'}`}
            >
              <span className="error-quicklink__icon" aria-hidden>
                <Icon />
              </span>
              <span className="error-quicklink__body">
                <span className="error-quicklink__label">{label}</span>
                <p className="error-quicklink__desc">{desc}</p>
              </span>
            </Link>
          ))}
        </StaggerReveal>
      </section>
    </>
  );
}
